// Pure view-scale maths for flag REACT_APP_LOGO_DRAG_RESIZE_KEEP_SCALE: the
// logo editor's "true scale" view, where the canvas keeps the px/mm the
// corner drag ran at instead of snapping back to the fit box when the resize
// response lands.

import { formBelongsToLogo, getPreviewScale } from "./previewScale";

/**
 * The size the fit box corresponds to. The canvas paints the bitmap in its
 * fit box (CSS scale 1) for the SAVED size, so anchoring at the saved mm of
 * the logo the drag started on gives exactly the px/mm the drag ran at:
 * a later size stays at form/anchor while the saved size moves underneath.
 */
export interface ScaleAnchor {
  logoId?: number;
  widthMm: number;
  heightMm: number;
}

/** Anchor for a logo with a known positive size; null when it has none. */
export function anchorFor(logo: {
  id?: number;
  widthMm?: number;
  heightMm?: number;
}): ScaleAnchor | null {
  const widthMm = logo.widthMm ?? 0;
  const heightMm = logo.heightMm ?? 0;
  if (!(widthMm > 0) || !(heightMm > 0)) return null;
  return { logoId: logo.id, widthMm, heightMm };
}

/**
 * Canvas scale for the true-scale view: form/anchor clamped to [0.1, 1] -
 * the logo never grows past its fit box (a drag past the box ends at the fit
 * box, exactly as "Fit to view" shows it). Falls back to the fit view
 * (getPreviewScale against the saved size - today's behaviour) without an
 * anchor, when the anchor belongs to another logo, or when the form does.
 */
export function getViewScale(
  form: { id?: number; widthMm: number; heightMm: number },
  logo: { id?: number; widthMm?: number; heightMm?: number },
  anchor: ScaleAnchor | null,
  maxScale: number
): number {
  if (
    !anchor ||
    (anchor.logoId !== undefined &&
      logo.id !== undefined &&
      anchor.logoId !== logo.id)
  ) {
    return getPreviewScale(form, logo, maxScale);
  }
  if (!formBelongsToLogo(form, logo)) return 1;
  return getPreviewScale(
    { widthMm: form.widthMm, heightMm: form.heightMm },
    { widthMm: anchor.widthMm, heightMm: anchor.heightMm },
    1
  );
}
